import { cva, VariantProps } from "class-variance-authority";
import React, { HTMLAttributes, Ref, ForwardRefRenderFunction } from "react";
import styles from "./button.module.scss";

export const buttonCva = cva([styles.btn], {
  variants: {
    buttonStyle: {
      primary: styles.primary,
      secondary: styles.secondary,
      default: styles.default,
      success: styles.success,
      danger: styles.danger,
      warning: styles.warning,
      twitter: styles.twitter,
      google: styles.google,
      facebook: styles.facebook,
      linkedin: styles.linkedin,
      xing: styles.xing,
      grayLight: styles.grayLight,
      grayDark: styles.grayDark,
    },
    block: {
      true: styles.block,
    },
    iconed: {
      true: styles.iconed,
    },
    fab: {
      true: styles.fab,
    },
    raised: {
      true: styles.raised,
    },
    bordered: {
      true: styles.bordered,
    },
    disabled: {
      true: styles.disabled,
    },
    size: {
      small: ["btn--sm"],
      medium: ["btn--lg"],
    },
    rounded: {
      true: styles.rounded,
    },
    dense: {
      true: styles.dense,
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

export interface ButtonProps
  extends HTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonCva> {
  /**
   * Text for the button
   *
   * @default null
   */
  children?: React.ReactNode;
  /**
   * Optional click handler
   */
  onClick?: (e?: any) => void;
  /**
   * The button type. The default is "submit" because most browsers have this default.
   *
   * @default "submit"
   */
  type?: "submit" | "button" | "reset" | undefined;
  /**
   * The button ref.
   */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Primary UI component for user interaction
 */
const ButtonComponent: ForwardRefRenderFunction<
  HTMLButtonElement,
  ButtonProps
> = (
  {
    children,
    className,
    buttonStyle,
    block,
    raised = true,
    iconed = false,
    fab = false,
    bordered = false,
    disabled,
    size,
    type = "submit",
    rounded,
    dense,
    ...props
  },
  ref
) => {
  return (
    <button
      type={type}
      className={buttonCva({
        buttonStyle,
        block,
        iconed,
        fab,
        raised,
        bordered,
        disabled,
        size,
        rounded,
        dense,
        class: className,
      })}
      disabled={!!disabled}
      ref={ref}
      {...props}
    >
      {children}
    </button>
  );
};

const Button = React.forwardRef(ButtonComponent);
export { Button };
export default Button;
